import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { customFieldsService } from '../api/custom-fields.service';
import { CUSTOM_FIELD_KEYS } from './queryKeys';
import type { ProjectFieldFilters } from '../types';

/**
 * Fetches the paginated list of custom field definitions for a project.
 *
 * @param projectId - The project whose fields to fetch.
 * @param filters - Optional pagination and search filters.
 * @returns TanStack Query result with `data.items` as `ProjectField[]`.
 *
 * @remarks
 * - Disabled until `activeOrganizationId` is available — returns idle state during org loading.
 * - `staleTime` is 5 minutes; fields change infrequently.
 * - Invalidated automatically when any field mutation (create/update/delete) succeeds.
 */
export function useProjectFields(projectId: string, filters?: ProjectFieldFilters) {
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  return useQuery({
    queryKey: CUSTOM_FIELD_KEYS.list(activeOrganizationId ?? '', projectId, filters),
    queryFn: ({ signal }) => customFieldsService.getProjectFields(projectId, filters, signal),
    staleTime: 300_000,
    enabled: !!activeOrganizationId && !!projectId,
  });
}
